import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { Database } from '../../lib/database.types';
import './Registration.css';
type SupervisorProfileInsert = Database['public']['Tables']['supervisor_profiles']['Insert'] & { id: string };
type StudentProfileInsert = Database['public']['Tables']['student_profile']['Insert'] & { id: string };

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [destination, setDestination] = useState('');

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        setVerificationStatus('Authentication successful, setting up your profile...');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Check if there's a verification code in the URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        
        let session = null;
        
        if (code) {
          setVerificationStatus('Processing email verification...');
          
          // Exchange the code for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            // Don't show error to user, just continue with verification
            setVerificationStatus('Processing email verification...');
          }
          
          if (data.session) {
            session = data.session;
            setVerificationStatus('Email verified! Setting up your profile...');
            
            // Clean up the URL by removing the code parameter
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('code');
            window.history.replaceState({}, '', newUrl.toString());
          } else {
            throw new Error('Failed to establish session after email verification');
          }
        }

        // Check for student registration data
        let pendingRegistration = localStorage.getItem('pendingRegistration');
        let isSupervisor = false;
        
        // If no student registration, check for supervisor registration
        if (!pendingRegistration) {
          pendingRegistration = localStorage.getItem('pendingSupervisorRegistration');
          isSupervisor = true;
          
          if (!pendingRegistration) {
            throw new Error('Registration data not found');
          }
        }

        const registrationData = JSON.parse(pendingRegistration);

        // Get the current session if we don't already have one from code exchange
        if (!session) {
          const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) throw sessionError;
          session = currentSession;
        }
        
        if (!session?.user) {
          console.error('No authenticated user found. Session:', session);
          throw new Error('No authenticated user found. Please try logging in again.');
        }
        
        // Check if email is already verified
        setVerificationStatus(
          session.user.email_confirmed_at 
            ? 'Your email is already verified. Setting up your profile...' 
            : 'Verifying your email...'
        );

        let profileError;
        
        if (isSupervisor) {
          // Check if profile already exists
          const { data: existingProfiles } = await supabase
            .from('supervisor_profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          // Create supervisor profile if it doesn't exist
          if (!existingProfiles) {
            
            const supervisorProfileData: SupervisorProfileInsert = {
              id: session.user.id,
              first_name: registrationData.first_name,
              last_name: registrationData.last_name,
              office: registrationData.office,
              role_title: registrationData.role_title,
              email: registrationData.email,
              user_role: registrationData.user_role
            };
            
            const { error } = await supabase
              .from('supervisor_profiles')
              .insert([supervisorProfileData] as any);
              
            profileError = error;
          }
          
          // Clear supervisor registration data in any case
          localStorage.removeItem('pendingSupervisorRegistration');
        } else {
          // Create student profile
          const studentProfileData: StudentProfileInsert = {
            id: session.user.id,
            first_name: registrationData.first_name,
            last_name: registrationData.last_name,
            student_id: registrationData.student_id,
            course_info: registrationData.course_info,
            department: registrationData.department,
            supervisor: registrationData.supervisor,
            email: registrationData.email,
            role: registrationData.role
          };
          
          const { error } = await supabase
            .from('student_profile')
            .insert([studentProfileData] as any);
            
          profileError = error;
          
          // Clear student registration data
          if (!error) {
            localStorage.removeItem('pendingRegistration');
          }
        }

        if (profileError) {
          throw profileError;
        }

        // Sign out the user so they need to log in manually
        await supabase.auth.signOut();

        // Set destination to login
        setDestination('/');
        setVerificationStatus('Email verified successfully! Your profile has been created. Redirecting to login...');
        
        // Add a slight delay before redirecting to show the success message
        setTimeout(() => {
          navigate('/', { 
            state: { message: 'Email verified successfully! Your profile has been created. You can now log in.' } 
          });
        }, 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred during verification');
      } finally {
        setVerifying(false);
      }
    };

    handleEmailVerification();
  }, [navigate, location]);

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="logo">
            <h1>Intern Trackr</h1>
            <p className="tagline">Anytime. Anywhere.</p>
          </div>
          <div className="verifying-message">
            <p>{verificationStatus || "Verifying your email..."}</p>
            {destination && (
              <div className="verification-progress">
                <div className="loader"></div>
                <p className="redirect-message">Redirecting you to login...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="logo">
            <h1>Intern Trackr</h1>
            <p className="tagline">Anytime. Anywhere.</p>
          </div>
          <div className="error-message">
            <p>Error verifying email: {error}</p>
            <button 
              className="login-button"
              onClick={() => navigate('/')}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifyEmail;
